import { inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { concatLatestFrom } from '@ngrx/operators';
import {
  BehaviorSubject,
  catchError,
  defer,
  EMPTY,
  filter,
  first,
  map,
  Observable,
  Subject,
  switchMap,
} from 'rxjs';

import {
  ComparisonOperator,
  LogicalQueryNode,
  LogicalType,
  QueryNodeType,
} from '@blaze-shared/api-contracts-domain';
import { DatasetRowsService } from '@blaze-shared/schema-data-access';
import { DatasetRow } from '@blaze-shared/schema-domain';
import { type Nullish } from '@blaze-shared/shared-util';

type RowState = 'Ready' | 'Loading' | 'Invalidated' | 'Unset' | 'Error';

interface SelectedRowState {
  datasetId?: string;
  rowIdColumn?: string;
  rowId?: string;
  row?: DatasetRow | null;
  state: RowState;
}

interface TriggerRowLoadRequest {
  datasetId: string;
  rowIdColumn: string;
  rowId: string;
}

/**
 * Service used by the dynamic search feature.
 * It fetches and caches the selected row separately because the dropdown's options list is fetch dynamically
 * and has 'max results' limitation so most often the selected value cannot be found in that list.
 */
@UntilDestroy()
@Injectable()
export class DropdownSelectedRowService {
  private datasetRowsService = inject(DatasetRowsService);

  private rowState$$ = new BehaviorSubject<SelectedRowState>({ state: 'Unset' });
  private triggerRowLoad$$ = new Subject<TriggerRowLoadRequest>();

  constructor() {
    this.initDatasetInvalidation();
    this.initTriggerReload();
  }

  /**
   * Fetches the provided row from database or state (if cache is ready and valid)
   * @param datasetId
   * @param rowIdColumn
   * @param rowId
   * @returns the data row, `null` if the row could not be found
   */
  getSelectedRow(
    datasetId: string,
    rowIdColumn: string,
    rowId: string,
  ): Observable<DatasetRow | null> {
    return defer(() => {
      this.triggerRowLoad$$.next({ datasetId, rowIdColumn, rowId });

      return this.rowState$$.pipe(
        // Wait for the row to be ready
        first((rowState) => rowState.state === 'Ready' && rowId === rowState.rowId),
        map((rowState) => rowState.row ?? null),
      );
    });
  }

  clearSelectedRow(): void {
    this.rowState$$.next({ state: 'Unset' });
  }

  updateSelectedRow(row: DatasetRow, rowIdField: string): void {
    // Update cache if the state is Ready and rowId matches - update the row contents
    if (
      this.rowState$$.value.state === 'Ready' &&
      this.rowState$$.value.rowId === row[rowIdField]
    ) {
      this.rowState$$.next({ ...this.rowState$$.value, row });
    }
  }

  private initDatasetInvalidation(): void {
    // Invalidate cache if the dataset got changed
    this.rowState$$
      .pipe(
        switchMap((rowState) => {
          if (rowState.state === 'Ready' && rowState.datasetId) {
            return this.datasetRowsService.observeDatasetAndRelatedChanged(rowState.datasetId);
          }

          return EMPTY;
        }),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.rowState$$.next({ ...this.rowState$$.value, state: 'Invalidated' });
      });
  }

  private initTriggerReload(): void {
    this.triggerRowLoad$$
      .pipe(
        concatLatestFrom(() => this.rowState$$),
        filter(([rowLoadRequest, rowState]) => {
          const isCurrentRow =
            rowState.datasetId === rowLoadRequest.datasetId &&
            rowState.rowIdColumn === rowLoadRequest.rowIdColumn &&
            rowState.rowId === rowLoadRequest.rowId;
          const isReadyOrLoading = rowState.state === 'Ready' || rowState.state === 'Loading';

          // Trigger load if the requested row is not ready or loading or a different row is requested
          return !(isCurrentRow && isReadyOrLoading);
        }),
        switchMap(([rowLoadRequest]) => {
          const query: LogicalQueryNode = {
            nodeType: QueryNodeType.Logical,
            logicalType: LogicalType.And,
            children: [
              {
                column: rowLoadRequest.rowIdColumn,
                displayInfos: [],
                isFormulaValue: false,
                isOptional: false,
                nodeType: QueryNodeType.Comparison,
                operator: ComparisonOperator.Eq,
                value: rowLoadRequest.rowId,
              },
            ],
          };

          // Set state to loading
          this.updateRowState(rowLoadRequest, 'Loading');

          return this.datasetRowsService
            .get({ datasetId: rowLoadRequest.datasetId, filterQuery: query, maxResults: 1 })
            .pipe(
              map((result) => ({
                row: result[0] ?? null,
                rowLoadRequest,
              })),
              catchError(() => {
                // Store the error in cache
                this.updateRowState(rowLoadRequest, 'Error');
                return EMPTY;
              }),
            );
        }),
        untilDestroyed(this),
      )
      .subscribe(({ row, rowLoadRequest }) => {
        // Store the result in cache
        this.updateRowState(rowLoadRequest, 'Ready', row);
      });
  }

  private updateRowState(
    rowLoadRequest: TriggerRowLoadRequest,
    state: RowState,
    row?: Nullish<DatasetRow>,
  ): void {
    this.rowState$$.next({
      state,
      datasetId: rowLoadRequest.datasetId,
      rowIdColumn: rowLoadRequest.rowIdColumn,
      rowId: rowLoadRequest.rowId,
      row,
    });
  }
}
